import { Viewer, Worker } from '@react-pdf-viewer/core';
import { defaultLayoutPlugin, ToolbarSlot, ToolbarProps } from '@react-pdf-viewer/default-layout';
import '@react-pdf-viewer/core/lib/styles/index.css';
import '@react-pdf-viewer/default-layout/lib/styles/index.css';
import { IconFileText } from '@tabler/icons-react';

interface PDFTabViewProps {
  pdfUrl: string;
  fileName?: string;
}

export const PDFTabView = ({ pdfUrl, fileName }: PDFTabViewProps) => {
  // Custom toolbar render function
  const renderToolbar = (Toolbar: (props: ToolbarProps) => React.ReactElement) => (
    <Toolbar>
      {(slots: ToolbarSlot) => {
        const {
          CurrentPageInput,
          GoToNextPage,
          GoToPreviousPage,
          NumberOfPages,
          ShowSearchPopover,
          Zoom,
          ZoomIn,
          ZoomOut,
          EnterFullScreen,
          Download,
        } = slots;
        return (
          <div className="flex items-center justify-between w-full px-2 py-1.5 bg-[#FAFAF9] border-b border-slate-200">
            {/* Left: Page Navigation */}
            <div className="flex items-center gap-1">
              <GoToPreviousPage />
              <div className="flex items-center gap-1 px-2">
                <CurrentPageInput /> 
                <span className="text-xs text-slate-400">of</span>
                <NumberOfPages />
              </div>
              <GoToNextPage />
            </div>

            {/* Center: Search & Zoom */}
            <div className="flex items-center gap-1">
              <ShowSearchPopover />
              <div className="w-px h-5 bg-slate-200 mx-2" />
              <ZoomOut />
              <Zoom />
              <ZoomIn />
            </div>

            {/* Right: Actions */}
            <div className="flex items-center gap-1">
              <Download />
              <EnterFullScreen />
            </div>
          </div>
        );
      }}
    </Toolbar>
  );

  // Create default layout plugin with customized toolbar
  const defaultLayoutPluginInstance = defaultLayoutPlugin({
    sidebarTabs: (defaultTabs) => [
      // Only keep thumbnails and bookmarks, remove attachments
      defaultTabs[0], // Thumbnails
      defaultTabs[1], // Bookmarks
    ],
    renderToolbar,
  });

  return (
    <div className="h-full flex flex-col">
      {/* File name header */}
      {fileName && (
        <div className="flex-shrink-0 flex items-center gap-2 px-4 py-2 bg-[#FAFAF9] border-b border-slate-100">
          <IconFileText className="w-4 h-4 text-slate-400" />
          <span className="text-sm font-medium text-slate-600 truncate">{fileName}</span>
        </div>
      )}
      
      {/* PDF Viewer */}
      <div className="flex-1 min-h-0">
        <Worker workerUrl="https://unpkg.com/pdfjs-dist@3.11.174/build/pdf.worker.min.js">
          <div className="h-full [&_.rpv-core__viewer]:h-full [&_.rpv-default-layout__container]:h-full">
            <Viewer
              fileUrl={pdfUrl}
              plugins={[defaultLayoutPluginInstance]}
              defaultScale={1}
            />
          </div>
        </Worker>
      </div>
    </div>
  );
};
